import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import RSIDTABLE from "./RSIDTABLE.tsx";
import PDBViewer from "./PDBStructure.tsx";
import FrequencyMap from "./FrequencyMap.tsx";
import LollipopPlot from "./LollipopPlot.tsx";
import MintDBLoader from "./MintDBLoader.tsx";

interface TargetData {
  target_name: string;
  gene_name: string;
  uniprot_id?: string;
  pdb_id?: string;
  is_gpcr?: boolean;
}

interface InvestigationalDrug {
  drug_name: string;
  gene_name: string;
  target_name: string;
  uniprot_id: string | null;
  pdb_id: string | null;
  is_gpcr: boolean;
  disorder_name: string;
}

interface InvMutation {
  rsid: string;
  mutation: string;
  position: number;
  original_aa: string;
  mutated_aa: string;
  classification?: string;
  uk_biobank?: string;
  disorder_name?: string;
}

interface InvMutationData {
  existing: InvMutation[];
  investigational: Record<string, InvMutation[]>;
}

const invClassificationColors: Record<string, { bg: string; text: string }> = {
  pathogenic: { bg: "#fef2f2", text: "#dc2626" },
  "likely pathogenic": { bg: "#fef2f2", text: "#dc2626" },
  benign: { bg: "#f0fdf4", text: "#16a34a" },
  "likely benign": { bg: "#f0fdf4", text: "#16a34a" },
  ambiguous: { bg: "#fefce8", text: "#ca8a04" },
};

// Frequency bar chart: shows max allele frequency per rsID at its protein position
function FrequencyBarPlot({ geneName, mutations, proteinLength, rows }: {
  geneName: string;
  mutations: { position: number; rsid: string; mutation: string; classification: string }[];
  proteinLength: number;
  rows?: any[];
}) {
  const [freqData, setFreqData] = useState<{ position: number; rsid: string; frequency: number; mutation: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [svgWidth, setSvgWidth] = useState(800);
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; rsid: string; freq: number; pos: number; mutation: string }>({
    visible: false, x: 0, y: 0, rsid: '', freq: 0, pos: 0, mutation: ''
  });

  // Range slider state
  const [rangeStart, setRangeStart] = useState(1);
  const [rangeEnd, setRangeEnd] = useState(50);
  const draggingThumb = useRef<'start' | 'end' | null>(null);
  const rangeTrackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      const container = wrapperRef.current || svgRef.current?.parentElement;
      if (container) {
        setSvgWidth(Math.max(400, (container.clientWidth || 800) - 20));
      }
    };
    updateWidth();
    // ResizeObserver also catches non-window layout changes (e.g. the
    // structure panel expanding) that a window 'resize' listener misses.
    const observer = new ResizeObserver(updateWidth);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    window.addEventListener('resize', updateWidth);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  useEffect(() => {
    // Build the per-gene frequency series from already-parsed gnomAD rows.
    const computeFromRows = (allRows: any[]) => {
      // Filter to this gene
      const geneRows = allRows.filter(r => r.Gene_Name === geneName);

      // Get mean frequency per rsID across all populations
      const rsidFreqs: Record<string, number[]> = {};
      geneRows.forEach(r => {
        if (r.rsID && String(r.rsID).trim()) {
          if (!rsidFreqs[r.rsID]) rsidFreqs[r.rsID] = [];
          rsidFreqs[r.rsID].push(typeof r.Frequency === 'number' ? r.Frequency : parseFloat(r.Frequency) || 0);
        }
      });
      const rsidMaxFreq: Record<string, number> = {};
      Object.entries(rsidFreqs).forEach(([rsid, freqs]) => {
        rsidMaxFreq[rsid] = freqs.reduce((a, b) => a + b, 0) / freqs.length;
      });

      // Match with mutation positions
      const combined: typeof freqData = [];
      const seen = new Set<string>();
      mutations.forEach(m => {
        const key = m.rsid;
        if (seen.has(key)) return;
        seen.add(key);
        if (rsidMaxFreq[m.rsid] !== undefined) {
          combined.push({
            position: m.position,
            rsid: m.rsid,
            frequency: rsidMaxFreq[m.rsid],
            mutation: m.mutation,
          });
        }
      });
      combined.sort((a, b) => a.position - b.position);
      setFreqData(combined);
    };

    // Use shared rows from the page if available; otherwise fall back to fetch.
    if (rows) {
      computeFromRows(rows);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const resp = await fetch(`/api/getData?type=frequency&gene=${encodeURIComponent(geneName)}`);
        const json = await resp.json();
        computeFromRows(Array.isArray(json.rows) ? json.rows : []);
      } catch (e) {
        console.error('Error loading frequency data:', e);
      }
      setLoading(false);
    };
    load();
  }, [geneName, mutations, rows]);

  if (loading) return <div className="text-center text-muted py-4">Loading frequency data...</div>;
  if (freqData.length === 0) return <div className="text-center text-muted py-4">No allele frequency data available for this gene</div>;

  const margin = { top: 40, right: 40, bottom: 45, left: 60 };
  const height = 300;
  const plotWidth = svgWidth - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const clampedStart = Math.max(1, Math.min(rangeStart, proteinLength));
  const clampedEnd = Math.max(clampedStart, Math.min(rangeEnd, proteinLength));
  const visible = freqData.filter(d => d.position >= clampedStart && d.position <= clampedEnd);

  // Group by position for stacking
  const stackedByPosition: Record<number, typeof freqData> = {};
  visible.forEach(d => {
    if (!stackedByPosition[d.position]) stackedByPosition[d.position] = [];
    stackedByPosition[d.position].push(d);
  });

  // Max stacked height (sum of frequencies at a position)
  const maxStackedFreq = Object.values(stackedByPosition).reduce((max, group) => {
    const sum = group.reduce((s, d) => s + d.frequency, 0);
    return Math.max(max, sum);
  }, 0);
  const yMax = maxStackedFreq > 0 ? maxStackedFreq * 1.15 : 1e-4;

  const xScale = (pos: number) => ((pos - clampedStart) / (clampedEnd - clampedStart || 1)) * plotWidth;
  const yScale = (freq: number) => plotHeight - (freq / yMax) * plotHeight;

  const uniquePositions = Object.keys(stackedByPosition).length;
  const barWidth = Math.max(2, Math.min(12, plotWidth / (uniquePositions || 1) * 0.6));

  // Colors for stacked segments
  const stackColors = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  // Y-axis ticks
  const yTicks = Array.from({ length: 5 }, (_, i) => (yMax / 4) * i);

  // X-axis ticks
  const xTickCount = 5;
  const xStep = Math.ceil((clampedEnd - clampedStart) / (xTickCount - 1));

  return (
    <div className="w-100 position-relative" ref={wrapperRef}>
      {/* Range slider */}
      {proteinLength > 1 && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
          <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.7rem', color: '#64748b' }}>
            <span style={{ fontWeight: 500 }}>Position Range:</span>
            <input type="number" min={1} max={proteinLength} value={clampedStart}
              onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) { setRangeStart(Math.max(1, Math.min(v, proteinLength))); } }}
              style={{ width: 56, border: '1px solid #e2e8f0', borderRadius: 4, padding: '0.2rem 0.3rem', fontSize: '0.75rem', fontWeight: 600, color: '#1A2940', textAlign: 'center', outline: 'none' }}
            />
            <span style={{ color: '#94a3b8' }}>–</span>
            <input type="number" min={1} max={proteinLength} value={clampedEnd}
              onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) { setRangeEnd(Math.max(1, Math.min(v, proteinLength))); } }}
              style={{ width: 56, border: '1px solid #e2e8f0', borderRadius: 4, padding: '0.2rem 0.3rem', fontSize: '0.75rem', fontWeight: 600, color: '#1A2940', textAlign: 'center', outline: 'none' }}
            />
            <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>({visible.length} variants)</span>
          </div>
          <div style={{ padding: '0.5rem 8px 0.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '0.625rem', color: '#94a3b8', fontWeight: 500 }}>1</span>
              <span style={{ fontSize: '0.625rem', color: '#94a3b8', fontWeight: 500 }}>{proteinLength}</span>
            </div>
            <div ref={rangeTrackRef} style={{ position: 'relative', height: 36, userSelect: 'none', cursor: 'pointer' }}
              onMouseDown={e => {
                if (!rangeTrackRef.current) return;
                const rect = rangeTrackRef.current.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                const val = Math.max(1, Math.round(pct * proteinLength));
                if (Math.abs(val - rangeStart) < Math.abs(val - rangeEnd)) {
                  setRangeStart(Math.min(val, rangeEnd)); draggingThumb.current = 'start';
                } else {
                  setRangeEnd(Math.max(val, rangeStart)); draggingThumb.current = 'end';
                }
                document.body.style.userSelect = 'none';
                const onMove = (ev: MouseEvent) => {
                  const r = rangeTrackRef.current?.getBoundingClientRect(); if (!r) return;
                  const p = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
                  const v = Math.max(1, Math.round(p * proteinLength));
                  if (draggingThumb.current === 'start') setRangeStart(Math.max(1, Math.min(v, rangeEnd)));
                  else setRangeEnd(Math.min(proteinLength, Math.max(v, rangeStart)));
                };
                const onUp = () => { draggingThumb.current = null; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
            >
              <div style={{ position: 'absolute', top: 14, left: 0, right: 0, height: 6, background: '#e2e8f0', borderRadius: 3 }} />
              <div style={{ position: 'absolute', top: 14, left: `${((clampedStart - 1) / proteinLength) * 100}%`, width: `${((clampedEnd - clampedStart + 1) / proteinLength) * 100}%`, height: 6, background: 'linear-gradient(90deg, #60a5fa, #3b82f6)', borderRadius: 3 }} />
              <div style={{ position: 'absolute', top: 7, left: `${((clampedStart - 1) / proteinLength) * 100}%`, transform: 'translateX(-50%)', cursor: 'grab' }}>
                <div style={{ width: 20, height: 20, background: 'linear-gradient(135deg, #fff, #f1f5f9)', border: '2.5px solid #3b82f6', borderRadius: '50%', boxShadow: '0 2px 6px rgba(59,130,246,0.3)' }} />
              </div>
              <div style={{ position: 'absolute', top: 7, left: `${(clampedEnd / proteinLength) * 100}%`, transform: 'translateX(-50%)', cursor: 'grab' }}>
                <div style={{ width: 20, height: 20, background: 'linear-gradient(135deg, #fff, #f1f5f9)', border: '2.5px solid #3b82f6', borderRadius: '50%', boxShadow: '0 2px 6px rgba(59,130,246,0.3)' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <svg ref={svgRef} width={svgWidth} height={height} style={{ maxWidth: '100%' }}>
        <text x={svgWidth / 2} y={20} textAnchor="middle" style={{ fontSize: 16, fontWeight: 600 }} fill="#1A2940">
          Mean Allele Frequency Across Protein Positions
        </text>
        <text x={svgWidth / 2} y={34} textAnchor="middle" style={{ fontSize: 10 }} fill="#94a3b8">
          Averaged across all gnomAD populations per variant
        </text>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {/* Y gridlines + labels */}
          {yTicks.map((tick, i) => (
            <g key={i}>
              <line x1={0} y1={yScale(tick)} x2={plotWidth} y2={yScale(tick)} stroke="#e5e7eb" strokeWidth={1} strokeDasharray={i > 0 ? "3,3" : "0"} />
              <text x={-8} y={yScale(tick) + 3} textAnchor="end" fontSize={9} fill="#64748b">
                {tick.toExponential(1)}
              </text>
            </g>
          ))}

          {/* Y axis label */}
          <text transform={`translate(-48, ${plotHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="#64748b" fontWeight={500}>
            Mean Allele Frequency
          </text>

          {/* X axis line */}
          <line x1={0} y1={plotHeight} x2={plotWidth} y2={plotHeight} stroke="#4b5563" strokeWidth={1.5} />

          {/* X ticks */}
          {Array.from({ length: xTickCount }, (_, i) => {
            const pos = clampedStart + i * xStep;
            if (pos > clampedEnd) return null;
            return (
              <text key={pos} x={xScale(pos)} y={plotHeight + 16} textAnchor="middle" fontSize={10} fill="#6b7280">
                {pos}
              </text>
            );
          })}
          <text x={plotWidth / 2} y={plotHeight + 35} textAnchor="middle" fontSize={11} fill="#64748b" fontWeight={500}>
            Protein Position
          </text>

          {/* Stacked Bars */}
          {Object.entries(stackedByPosition).map(([pos, group]) => {
            const x = xScale(Number(pos)) - barWidth / 2;
            let cumHeight = 0;
            return group.map((d, i) => {
              const segH = d.frequency > 0 ? Math.max(1, (d.frequency / yMax) * plotHeight) : 0;
              const y = plotHeight - cumHeight - segH;
              cumHeight += segH;
              return (
                <rect
                  key={`${pos}-${i}`}
                  x={x}
                  y={y}
                  width={barWidth}
                  height={segH}
                  fill={stackColors[i % stackColors.length]}
                  opacity={0.85}
                  rx={i === group.length - 1 ? 1 : 0}
                  style={{ cursor: 'pointer', transition: 'opacity 0.1s' }}
                  onMouseEnter={e => {
                    (e.target as SVGRectElement).setAttribute('opacity', '1');
                    const rect = wrapperRef.current?.getBoundingClientRect();
                    if (rect) setTooltip({ visible: true, x: e.clientX - rect.left, y: e.clientY - rect.top - 10, rsid: d.rsid, freq: d.frequency, pos: d.position, mutation: d.mutation });
                  }}
                  onMouseLeave={e => {
                    (e.target as SVGRectElement).setAttribute('opacity', '0.85');
                    setTooltip(prev => ({ ...prev, visible: false }));
                  }}
                />
              );
            });
          })}
        </g>
      </svg>

      {/* Tooltip */}
      {tooltip.visible && (
        <div className="position-absolute bg-dark text-white p-2 rounded shadow-lg"
          style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)', fontSize: 12, zIndex: 1000, whiteSpace: 'nowrap' }}>
          <div><strong>RSID:</strong> {tooltip.rsid}</div>
          <div><strong>Mutation:</strong> {tooltip.mutation}</div>
          <div><strong>Position:</strong> {tooltip.pos}</div>
          <div><strong>Mean Frequency:</strong> {tooltip.freq.toExponential(4)}</div>
        </div>
      )}
    </div>
  );
}

function Disorder() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const geneParam = searchParams.get('gene');

  // Sidebar state
  const [disorderTargets, setDisorderTargets] = useState<TargetData[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(true);
  const [variantCounts, setVariantCounts] = useState<Record<string, number>>({});
  const [selectedGene, setSelectedGene] = useState<string | null>(null);

  // Target detail state
  const [targetData, setTargetData] = useState<any[]>([]);
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetError, setTargetError] = useState(false);

  // gnomAD allele-frequency rows for the selected gene, fetched from the DB
  // as part of the target detail load (gated by targetLoading) and shared with
  // the map, frequency plot and table — replacing the old 5MB CSV download.
  const [gnomadRows, setGnomadRows] = useState<any[] | null>(null);

  // Protein domain annotations, also loaded once at the page level and shared
  // with the lollipop plot and table (previously each fetched /api/domains).
  const [domains, setDomains] = useState<any[] | null>(null);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [drugs, setDrugs] = useState<any[]>([]);
  const [ligands, setLigands] = useState<any[]>([]);
  const [ligandsLoading, setLigandsLoading] = useState(true);
  const [targetDescription, setTargetDescription] = useState("");
  const [targetReferenceLink, setTargetReferenceLink] = useState("");
  const [isGPCR, setIsGPCR] = useState(false);

  // Investigational drugs state
  const [invDrugs, setInvDrugs] = useState<InvestigationalDrug[]>([]);
  const [invMutations, setInvMutations] = useState<InvMutationData | null>(null);
  const [invMutLoading, setInvMutLoading] = useState(false);
  const [expandedInvDrug, setExpandedInvDrug] = useState<string | null>(null);
  const [invSortCol, setInvSortCol] = useState<"position" | "classification" | "uk_biobank" | "source">("source");
  const [invSortDir, setInvSortDir] = useState<"asc" | "desc">("asc");
  const [invSearch, setInvSearch] = useState("");

  // Tab state (kept for compatibility)
  const currentTab = "overview";

  // Lollipop / Frequency chart toggle.
  // `mountedPlots` keeps each plot alive once it has been opened so switching
  // tabs just flips visibility instead of re-fetching/re-parsing data.
  const [plotView, setPlotView] = useState<'lollipop' | 'frequency'>('lollipop');
  const [mountedPlots, setMountedPlots] = useState<Set<'lollipop' | 'frequency'>>(
    () => new Set(['lollipop'])
  );
  const selectPlotView = (view: 'lollipop' | 'frequency') => {
    setPlotView(view);
    setMountedPlots((prev) => (prev.has(view) ? prev : new Set(prev).add(view)));
  };

  // Shared filter state for LollipopPlot and RSIDTABLE
  const [sharedFilters, setSharedFilters] = useState({
    searchTerm: "",
    classificationFilter: "all",
    domainFilter: "all",
    ukBiobankFilter: "all",
    polymorphicFilter: "all",
  });

  // Mobile sidebar toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Collapsible right-hand protein structure panel.
  // `showStructure` controls whether the fixed-width panel is expanded.
  // `structureMounted` stays true after the first open so Mol* isn't
  // re-downloaded every time the panel is toggled.
  const [showStructure, setShowStructure] = useState(false);
  const [structureMounted, setStructureMounted] = useState(false);

  const toggleStructure = () => {
    setShowStructure((prev) => {
      const next = !prev;
      if (next) setStructureMounted(true);
      return next;
    });
  };

  function capitalizeFirstLetter(str: string | undefined) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // --- Data fetching functions for selected target ---

  const getDrugs = useCallback(async (geneName: string, disorder: string) => {
    try {
      const query = `
        SELECT DISTINCT d.drug_name
        FROM drugs d
        JOIN targets t ON d.id = t.drug_id
        JOIN disorders dis ON d.disorder_id = dis.id
        WHERE t.gene_name = '${geneName}' AND dis.name = '${disorder}' AND (d.is_investigational IS NOT TRUE)
        ORDER BY d.drug_name
      `;
      const response = await fetch("/api/execute-query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });
      if (!response.ok) throw new Error("Failed to fetch drugs");
      const data = await response.json();
      setDrugs(data.results || []);
    } catch (error) {
      console.error("Error fetching drugs:", error);
      setDrugs([]);
    }
  }, []);

  const getLigands = useCallback(async (geneName: string) => {
    try {
      setLigandsLoading(true);
      const response = await fetch(`/api/ligands?gene_name=${encodeURIComponent(geneName)}`);
      if (!response.ok) {
        if (response.status === 404) { setLigands([]); return; }
        throw new Error("Failed to fetch ligands");
      }
      const data = await response.json();
      setLigands(data.ligands || []);
    } catch (error) {
      console.error("Error fetching ligands:", error);
      setLigands([]);
    } finally {
      setLigandsLoading(false);
    }
  }, []);

  // Fetch gnomAD allele-frequency rows for a single gene from the DB
  // (replaces downloading/parsing the whole 5MB gnomad_data.csv client-side).
  const getFrequencyData = useCallback(async (geneName: string) => {
    try {
      const response = await fetch(`/api/getData?type=frequency&gene=${encodeURIComponent(geneName)}`);
      if (!response.ok) { setGnomadRows([]); return; }
      const data = await response.json();
      setGnomadRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (error) {
      console.error("Error fetching frequency data:", error);
      setGnomadRows([]);
    }
  }, []);

  const getTargetDescription = useCallback(async (geneName: string) => {
    try {
      const query = `
        SELECT description, link
        FROM target_descriptions
        WHERE UPPER(gene) = UPPER('${geneName}')
        LIMIT 1
      `;
      const response = await fetch("/api/execute-query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });
      if (!response.ok) throw new Error("Failed to fetch target description");
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        setTargetDescription(data.results[0].description);
        setTargetReferenceLink(data.results[0].link || "");
      } else {
        setTargetDescription("No description available for this gene.");
        setTargetReferenceLink("");
      }
    } catch (error) {
      console.error("Error fetching target description:", error);
      setTargetDescription("Description unavailable.");
      setTargetReferenceLink("");
    }
  }, []);


  const fetchInvestigationalDrugs = useCallback(async (geneName: string) => {
    try {
      const response = await fetch("/api/investigational-targets");
      if (!response.ok) { setInvDrugs([]); return; }
      const data: InvestigationalDrug[] = await response.json();
      const forGene = data.filter((d) => d.gene_name.toLowerCase() === geneName.toLowerCase());
      setInvDrugs(forGene);
    } catch {
      setInvDrugs([]);
    }
  }, []);

  const fetchInvMutations = useCallback(async (geneName: string) => {
    if (invMutations) return;
    setInvMutLoading(true);
    try {
      const resp = await fetch(`/api/investigational-targets?gene=${encodeURIComponent(geneName)}`);
      const data = await resp.json();
      setInvMutations({
        existing: Array.isArray(data.existing) ? data.existing : [],
        investigational: (data.investigational && typeof data.investigational === "object") ? data.investigational : {},
      });
    } catch {
      setInvMutations({ existing: [], investigational: {} });
    } finally {
      setInvMutLoading(false);
    }
  }, [invMutations]);

  const fetchTargetDetail = useCallback(async (geneName: string, targetName: string, disorder: string) => {
    try {
      const response = await fetch(
        `/api/getData?type=target&targetName=${encodeURIComponent(targetName)}&disorder=${encodeURIComponent(disorder)}`
      );
      const json = await response.json();
      setTargetData([json]);
    } catch (err) {
      console.error("Error fetching target data:", err);
      setTargetData([]);
      setTargetError(true);
    }
  }, []);

  // Load all detail for a selected target
  const loadTargetDetail = useCallback(
    async (target: TargetData) => {
      if (!id) return;
      setTargetLoading(true);
      setTargetError(false);
      setSharedFilters({ searchTerm: "", classificationFilter: "all", domainFilter: "all", ukBiobankFilter: "all", polymorphicFilter: "all" });
      setIsGPCR(target.is_gpcr || false);
      setInvDrugs([]);
      setInvMutations(null);
      setExpandedInvDrug(null);
      setInvSearch("");
      setInvSortCol("source");
      setInvSortDir("asc");

      setGnomadRows(null);

      await Promise.all([
        fetchTargetDetail(target.gene_name, target.target_name, id),
        getDrugs(target.gene_name, id),
        getLigands(target.gene_name),
        getTargetDescription(target.gene_name),
        fetchInvestigationalDrugs(target.gene_name),
        getFrequencyData(target.gene_name),
      ]);

      setTargetLoading(false);
    },
    [id, fetchTargetDetail, getDrugs, getLigands, getTargetDescription, fetchInvestigationalDrugs, getFrequencyData]
  );

  // --- Fetch disorder targets on mount ---
  useEffect(() => {
    setTargetsLoading(true);
    setSelectedGene(null);
    setTargetData([]);

    const fetchDisorderData = async () => {
      try {
        const response = await fetch("/api/disorders?disorder=" + id);
        const json = await response.json();
        setDisorderTargets(json);

        // Fetch variant counts
        if (json.length > 0) {
          const geneNames = json.map((t: TargetData) => t.gene_name).join(",");
          try {
            const countResp = await fetch(`/api/getData?type=variantCount&gene_names=${geneNames}`);
            const countResult = await countResp.json();
            setVariantCounts(countResult.counts || {});
          } catch {
            setVariantCounts({});
          }
        }

        // Auto-select target from query param, or first target
        if (json.length > 0) {
          const match = geneParam && json.find((t: TargetData) => t.gene_name.toLowerCase() === geneParam.toLowerCase());
          setSelectedGene(match ? match.gene_name : json[0].gene_name);
        }
      } catch (err) {
        console.error("Error fetching disorder data:", err);
        setDisorderTargets([]);
      } finally {
        setTargetsLoading(false);
      }
    };

    fetchDisorderData();
  }, [id, geneParam]);

  // Load detail when selectedGene changes
  useEffect(() => {
    if (!selectedGene || disorderTargets.length === 0) return;
    const target = disorderTargets.find((t) => t.gene_name === selectedGene);
    if (target) {
      loadTargetDetail(target);
    }
  }, [selectedGene, disorderTargets, loadTargetDetail]);

  // Load protein domain annotations once for the whole page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/api/domains');
        const data = resp.ok ? await resp.json() : [];
        if (!cancelled) { setDomains(Array.isArray(data) ? data : []); setDomainsLoading(false); }
      } catch (e) {
        console.error('Error fetching domain data:', e);
        if (!cancelled) { setDomains([]); setDomainsLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --- Mutation helpers (from TargetOverview) ---
  // Memoized so the array/length keep a stable reference across re-renders.
  // The plots stay mounted, and their data effects depend on these props — an
  // unstable reference would make them re-fetch/re-parse on every render.
  const preparedMutations = useMemo(() => {
    if (!targetData[0]?.mutations) return [] as any[];
    const mutations = targetData[0].mutations;
    const mutationArray: any[] = [];

    const extractPosition = (mutation: string) => {
      const match = mutation.match(/(\d+)/);
      return match ? parseInt(match[1]) : null;
    };

    Object.entries(mutations).forEach(([rsid, mutationsList]: [string, any]) => {
      const ukBiobank = targetData[0]?.rsids?.[rsid]?.uk_biobank;
      mutationsList.forEach((mutationObj: any) => {
        const position = extractPosition(mutationObj.mutation);
        if (position) {
          mutationArray.push({
            position,
            rsid,
            mutation: mutationObj.mutation,
            classification: mutationObj.prediction?.classification || "no prediction",
            ukBiobank: ukBiobank || null,
          });
        }
      });
    });
    return mutationArray;
  }, [targetData]);

  const proteinLength = useMemo(() => {
    if (preparedMutations.length === 0) return 500;
    const maxPosition = Math.max(...preparedMutations.map((m) => m.position));
    return Math.max(maxPosition + 50, 500);
  }, [preparedMutations]);

  // Handle sidebar target click
  const handleTargetClick = (gene: string) => {
    if (gene === selectedGene) return;
    setSelectedGene(gene);
    setSidebarOpen(false);
  };

  // --- Loading state ---
  // Show the branded loader until *all* page data is ready: the disorder's
  // target list, and the selected target's full detail (mutations, drugs,
  // ligands, description, investigational drugs all resolve together in
  // loadTargetDetail). `detailPending` covers the brief gap between a target
  // being selected and its detail fetch starting, so the page never flashes
  // with unloaded data. `targetError` prevents the loader from hanging if a
  // fetch fails — the page then renders its "no data" state instead.
  const detailPending = !!selectedGene && targetData.length === 0 && !targetError;
  if (targetsLoading || targetLoading || detailPending || domainsLoading) {
    return <MintDBLoader />;
  }

  const selectedTarget = disorderTargets.find((t) => t.gene_name === selectedGene);

  return (
    <>
      <style>{`
        body, * {
          font-family: Segoe UI, Arial, Helvetica Neue, Helvetica, sans-serif !important;
        }

        .disorder-layout {
          display: flex;
          height: calc(100vh - 80px);
          overflow: hidden;
        }

        /* Sidebar */
        .disorder-sidebar {
          width: 240px;
          min-width: 240px;
          background: #f9fafb;
          border-right: 1px solid #e5e7eb;
          height: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          flex-shrink: 0;
        }

        .disorder-sidebar::-webkit-scrollbar {
          width: 3px;
        }
        .disorder-sidebar::-webkit-scrollbar-track {
          background: transparent;
        }
        .disorder-sidebar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 2px;
        }

        .sidebar-header {
          padding: 1.25rem 0.875rem 1rem;
          border-bottom: 2px solid #60a5fa;
          background: #f0f4f8;
        }

        .sidebar-header h2 {
          font-size: 1.1rem;
          font-weight: 800;
          color: #1A2940;
          margin: 0 0 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .sidebar-header p {
          font-size: 0.75rem;
          color: #64748b;
          margin: 0;
        }

        .target-list {
          padding: 0.25rem 0;
        }

        .target-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.875rem;
          cursor: pointer;
          transition: all 0.12s ease;
          border-left: 2px solid transparent;
          text-decoration: none;
          color: inherit;
        }

        .target-item:hover {
          background: #f0f4f8;
          border-left-color: #cbd5e1;
        }

        .target-item.active {
          background: #eef2ff;
          border-left-color: #60a5fa;
        }

        .target-item-thumb {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          flex-shrink: 0;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .target-item-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .target-item-info {
          flex: 1;
          min-width: 0;
        }

        .target-gene-badge {
          display: inline-block;
          background: #1A2940;
          color: #fff;
          font-size: 0.625rem;
          font-weight: 600;
          padding: 0.1rem 0.375rem;
          border-radius: 3px;
          letter-spacing: 0.03em;
          line-height: 1.2;
        }

        .target-item-name {
          font-size: 0.6875rem;
          color: #64748b;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-top: 0.125rem;
        }

        .target-item.active .target-gene-badge {
          background: #2563eb;
        }

        .target-item.active .target-item-name {
          color: #1e293b;
        }

        .target-variant-count {
          font-size: 0.625rem;
          color: #cbd5e1;
          flex-shrink: 0;
        }

        .target-item.active .target-variant-count {
          color: #3b82f6;
          font-weight: 600;
        }

        /* Main content */
        .disorder-main {
          flex: 1;
          min-width: 0;
          background: #fff;
          overflow: hidden;
        }

        .target-detail-loader {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
        }

        /* Split layout: left tab content + right structure */
        .detail-split {
          display: flex;
          height: 100%;
          overflow: hidden;
          position: relative;
        }

        .detail-left {
          min-width: 0;
          overflow-y: auto;
          overflow-x: hidden;
          flex: 1 1 auto;
        }

        /* Collapsible structure panel: fixed width when open, 0 when closed */
        .detail-right {
          width: 0;
          min-width: 0;
          display: flex;
          flex-direction: column;
          background: #f9fafb;
          overflow: hidden;
          flex: none;
          border-left: 1px solid #e5e7eb;
          transition: width 0.25s ease;
        }

        .detail-right.open {
          width: 600px;
          max-width: 55vw;
        }

        /* Vertical "Show Target" tab pinned to the right edge */
        .structure-toggle {
          position: absolute;
          top: 50%;
          right: 0;
          transform: translateY(-50%);
          z-index: 6;
          display: flex;
          align-items: center;
          gap: 7px;
          writing-mode: vertical-rl;
          padding: 18px 9px;
          background: #1A2940;
          color: #60a5fa;
          border: none;
          /* Bright accent stripe down the leading edge */
          border-left: 3px solid #60a5fa;
          border-top-left-radius: 12px;
          border-bottom-left-radius: 12px;
          font-size: 0.8125rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          cursor: pointer;
          box-shadow: -3px 0 14px rgba(15, 23, 42, 0.22);
          transition: right 0.25s ease, color 0.18s ease, background 0.18s ease, padding 0.18s ease, box-shadow 0.18s ease;
        }

        .structure-toggle:hover {
          background: #243650;
          color: #93c5fd;
          padding-right: 13px;
          box-shadow: -4px 0 18px rgba(96, 165, 250, 0.35);
        }

        .structure-toggle.open {
          right: min(600px, 55vw);
          color: #cbd5e1;
          border-left-color: #94a3b8;
        }

        .structure-toggle .chevron {
          writing-mode: horizontal-tb;
          font-size: 0.8rem;
          line-height: 1;
        }

        .structure-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.625rem 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .structure-header h3 {
          color: #0f172a;
          font-size: 0.8125rem;
          font-weight: 700;
          margin: 0;
        }

        .structure-viewer {
          flex: 1;
          min-height: 0;
        }

        .structure-viewer > div {
          height: 100% !important;
        }

        /* Tab bar */
        .detail-tabs {
          display: flex;
          gap: 0;
          border-bottom: 1px solid #e5e7eb;
          padding: 0 1.5rem;
        }

        .detail-tab {
          padding: 0.625rem 1rem;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #9ca3af;
          border: none;
          background: none;
          cursor: pointer;
          position: relative;
          transition: color 0.15s;
        }

        .detail-tab:hover {
          color: #374151;
        }

        .detail-tab.active {
          color: #1A2940;
          font-weight: 600;
        }

        .detail-tab.active::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -1px;
          height: 2px;
          background: #60a5fa;
          border-radius: 1px 1px 0 0;
        }

        /* Detail content area */
        .detail-content {
          padding: 1.5rem;
        }

        /* Overview/Drugs/Ligands (left) beside Geographical map (right).
           Wraps to a single column when the panel gets too narrow. */
        .overview-map-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }
        .overview-map-left {
          flex: 1 1 320px;
          min-width: 0;
        }
        .overview-map-right {
          flex: 1 1 360px;
          min-width: 0;
        }

        .detail-header {
          margin-bottom: 1rem;
        }

        .detail-header h1 {
          color: #0f172a;
          font-size: 1.375rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
        }

        .detail-header .subtitle {
          color: #64748b;
          font-size: 0.8125rem;
        }

        .gpcr-badge {
          background-color: #7c3aed;
          color: white;
          font-size: 0.625rem;
          font-weight: 600;
          padding: 0.15rem 0.375rem;
          border-radius: 3px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .section-title {
          color: #1e293b;
          font-size: 0.75rem;
          font-weight: 700;
          margin-bottom: 0.375rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .overview-text {
          color: #475569;
          font-size: 0.8125rem;
          line-height: 1.6;
          font-style: italic;
          margin-bottom: 0.75rem;
          text-align: justify;
        }

        .at-glance-table {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .at-glance-table th {
          color: #64748b;
          background: #f8fafc;
          border: none;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 0.375rem 0.5rem;
        }

        .at-glance-table td {
          color: #0f172a;
          font-weight: 500;
          font-size: 0.8125rem;
          padding: 0.375rem 0.5rem;
          border: none;
        }

        .no-data-message {
          color: #94a3b8;
          font-style: italic;
          text-align: center;
          padding: 0.75rem;
          font-size: 0.8125rem;
        }

        .uniprot-link {
          background-color: #dbeafe;
          color: #1e40af;
          font-weight: 600;
          font-size: 0.6875rem;
          padding: 0.1rem 0.375rem;
          border-radius: 3px;
          text-decoration: none;
          transition: background-color 0.2s ease;
        }

        .uniprot-link:hover {
          background-color: #bfdbfe;
          color: #1e3a8a;
        }

        .external-link {
          color: #3b82f6;
          font-size: 0.75rem;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .external-link:hover {
          color: #2563eb;
          text-decoration: underline;
        }

        /* Mobile toggle */
        .sidebar-mobile-toggle {
          display: none;
          position: fixed;
          bottom: 1rem;
          left: 1rem;
          z-index: 1050;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #1A2940;
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          font-size: 1rem;
          cursor: pointer;
          align-items: center;
          justify-content: center;
        }

        .sidebar-overlay {
          display: none;
        }

        @media (max-width: 991px) {
          .disorder-sidebar {
            position: fixed;
            left: 0;
            top: 0;
            z-index: 1040;
            transform: translateX(-100%);
            transition: transform 0.25s ease;
            height: 100vh;
          }

          .disorder-sidebar.open {
            transform: translateX(0);
          }

          .sidebar-mobile-toggle {
            display: flex;
          }

          .sidebar-overlay {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.25);
            z-index: 1035;
          }

          .sidebar-overlay.open {
            display: block;
          }

          .detail-content {
            padding: 1rem;
          }

          .detail-tabs {
            padding: 0 1rem;
          }

          .detail-split {
            flex-direction: column;
          }

          .detail-left {
            flex: none !important;
            border-right: none;
            border-bottom: 1px solid #e5e7eb;
          }

          /* On mobile the panel expands vertically instead of horizontally */
          .detail-right {
            order: -1;
            width: 100% !important;
            min-width: 0;
            height: 0;
            flex: none !important;
            border-left: none;
            border-bottom: 1px solid #e5e7eb;
            transition: height 0.25s ease;
          }

          .detail-right.open {
            width: 100% !important;
            height: 360px;
          }

          .structure-toggle {
            top: auto;
            bottom: 1rem;
            right: 1rem;
            transform: none;
            writing-mode: horizontal-tb;
            border-radius: 8px;
          }

          .structure-toggle.open {
            right: 1rem;
          }
        }
      `}</style>

      {/* Mobile sidebar overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Mobile sidebar toggle button */}
      <button
        className="sidebar-mobile-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        title="Toggle target list"
      >
        {sidebarOpen ? "\u2715" : "\u2630"}
      </button>

      <div className="disorder-layout">
        {/* Left Sidebar */}
        <aside className={`disorder-sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="sidebar-header">
            <h2>{capitalizeFirstLetter(id)}</h2>
            <p>{disorderTargets.length} target{disorderTargets.length !== 1 ? "s" : ""}</p>
          </div>

          <div className="target-list">
            {disorderTargets.map((target) => (
              <div
                key={target.gene_name}
                className={`target-item ${selectedGene === target.gene_name ? "active" : ""}`}
                onClick={() => handleTargetClick(target.gene_name)}
              >
                <div className="target-item-thumb">
                  {target.pdb_id ? (
                    <img
                      src={`https://cdn.rcsb.org/images/structures/${target.pdb_id.toLowerCase()}_assembly-1.jpeg`}
                      alt={target.gene_name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <span style={{ color: "#d1d5db", fontSize: "0.55rem" }}>N/A</span>
                  )}
                </div>

                <div className="target-item-info">
                  <span className="target-gene-badge">{target.gene_name}</span>
                  <div className="target-item-name" title={target.target_name}>
                    {target.target_name}
                  </div>
                </div>

                <span className="target-variant-count">
                  {variantCounts[target.gene_name] !== undefined
                    ? `${variantCounts[target.gene_name]} RSIDs`
                    : "..."}
                </span>
              </div>
            ))}
          </div>
        </aside>

        {/* Right Content */}
        <div className="disorder-main">
          {!selectedTarget ? (
            <div className="target-detail-loader">
              <p style={{ color: "#6b7280" }}>Select a target from the sidebar</p>
            </div>
          ) : targetLoading ? (
            <div className="target-detail-loader">
              <div className="text-center">
                <div
                  className="spinner-border mb-3"
                  role="status"
                  style={{
                    width: "3rem",
                    height: "3rem",
                    borderWidth: "4px",
                    borderColor: "#1A2940",
                    borderTopColor: "transparent",
                  }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                  Loading {selectedGene} data...
                </div>
              </div>
            </div>
          ) : targetData.length > 0 ? (
            <>
              {/* Two-column layout: tab content left, structure right */}
              <div className="detail-split">
                {/* Left: header + content */}
                <div className="detail-left">
                  <div className="detail-content">
                    {/* Header */}
                    <div className="detail-header">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h1>{selectedGene}</h1>
                        {isGPCR && <span className="gpcr-badge">GPCR</span>}
                        {targetData[0]?.uniprot_id && (
                          <a
                            href={`https://www.uniprot.org/uniprot/${targetData[0].uniprot_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="uniprot-link ms-1"
                          >
                            {targetData[0].uniprot_id}
                          </a>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <span className="subtitle">{selectedTarget.target_name}</span>
                        <a
                          href={`https://www.ncbi.nlm.nih.gov/gene?Db=gene&Cmd=DetailsSearch&Term=${selectedGene}`}
                          target="_blank"
                          className="external-link"
                          rel="noreferrer"
                        >
                          NCBI
                        </a>
                      </div>
                    </div>

                    <hr style={{ borderColor: "#e5e7eb" }} />

                    {/* Left column (Overview + Drugs/Ligands) beside the map */}
                    <div className="overview-map-grid">
                      <div className="overview-map-left">
                    {/* Overview */}
                    <section className="mb-3">
                      <h2
                        style={{
                          color: "#0f172a",
                          fontSize: "0.9375rem",
                          fontWeight: 700,
                          marginBottom: "0.5rem",
                        }}
                      >
                        Overview
                      </h2>
                      <p className="overview-text">
                        {targetDescription || "Loading description..."}
                        {targetReferenceLink && (
                          <a
                            href={targetReferenceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#60a5fa",
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              marginLeft: "0.5rem",
                              opacity: 0.8,
                              transition: "opacity 0.2s",
                              verticalAlign: "baseline",
                            }}
                            onMouseEnter={(e) =>
                              ((e.currentTarget as HTMLAnchorElement).style.opacity = "1")
                            }
                            onMouseLeave={(e) =>
                              ((e.currentTarget as HTMLAnchorElement).style.opacity = "0.8")
                            }
                            title="View reference"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </a>
                        )}
                      </p>
                    </section>

                    {/* Drugs & Ligands */}
                    <section className="mb-3">
                      <div className="mb-3">
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.375rem" }}>
                          <div className="section-title" style={{ margin: 0 }}>Associated Drugs</div>
                          <span style={{ fontSize: "0.6875rem", color: "#64748b", fontWeight: 500 }}>
                            {variantCounts[selectedGene!] !== undefined
                              ? `${variantCounts[selectedGene!]} RSIDs`
                              : ""}
                          </span>
                        </div>
                        <div className="table-responsive">
                          <table className="table at-glance-table mb-0">
                            <thead>
                              <tr>
                                <th>Drug</th>
                                <th>Type</th>
                              </tr>
                            </thead>
                            <tbody>
                              {drugs.length > 0 ? (
                                drugs.map((item) => (
                                  <tr key={item.drug_name}>
                                    <td>{item.drug_name}</td>
                                    <td>
                                      <span style={{
                                        display: "inline-block",
                                        padding: "0.1rem 0.4rem",
                                        borderRadius: "3px",
                                        fontSize: "0.6875rem",
                                        fontWeight: 600,
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                      }}>Approved</span>
                                    </td>
                                  </tr>
                                ))
                              ) : null}
                              {invDrugs.length > 0 && (() => {
                                const uniqueDrugs = Array.from(new Map(invDrugs.map(d => [d.drug_name, d])).values());
                                return uniqueDrugs.map((inv) => {
                                  const isExpanded = expandedInvDrug === inv.drug_name;
                                  return (
                                    <React.Fragment key={`inv-${inv.drug_name}`}>
                                      <tr
                                        style={{ cursor: "pointer", transition: "background 0.12s" }}
                                        onClick={() => {
                                          if (isExpanded) {
                                            setExpandedInvDrug(null);
                                          } else {
                                            setExpandedInvDrug(inv.drug_name);
                                            if (!invMutations) fetchInvMutations(inv.gene_name);
                                          }
                                        }}
                                      >
                                        <td style={{ fontWeight: 500 }}>
                                          <span style={{
                                            fontSize: "0.7rem",
                                            color: "#94a3b8",
                                            transition: "transform 0.2s ease",
                                            display: "inline-block",
                                            transform: isExpanded ? "rotate(90deg)" : "none",
                                            marginRight: "0.375rem",
                                          }}>&#9654;</span>
                                          {inv.drug_name}
                                        </td>
                                        <td>
                                          <span style={{
                                            display: "inline-block",
                                            padding: "0.1rem 0.4rem",
                                            borderRadius: "3px",
                                            fontSize: "0.6875rem",
                                            fontWeight: 600,
                                            backgroundColor: "#eef2ff",
                                            color: "#4338ca",
                                          }}>Investigational</span>
                                        </td>
                                      </tr>
                                      {isExpanded && (
                                        <tr>
                                          <td colSpan={2} style={{ padding: 0, border: "none" }}>
                                            <div style={{
                                              background: "#f8fafc",
                                              border: "1px solid #e2e8f0",
                                              borderRadius: "0 0 0.5rem 0.5rem",
                                              padding: "0.75rem 1rem",
                                              margin: "0 0 0.5rem",
                                            }}>
                                              {invMutLoading ? (
                                                <div style={{ textAlign: "center", padding: "0.75rem", color: "#94a3b8", fontSize: "0.8125rem" }}>
                                                  Loading mutations...
                                                </div>
                                              ) : invMutations && (invMutations.existing.length > 0 || Object.keys(invMutations.investigational).length > 0) ? (() => {
                                                // Combine all mutations into a single table
                                                type InvRow = { rsid: string; mutation: string; position: number; original_aa: string; mutated_aa: string; classification?: string; uk_biobank?: string; disorder: string; source: "Existing" | "Investigational" };
                                                const allRows: InvRow[] = [];
                                                invMutations.existing.forEach((m) => {
                                                  allRows.push({ ...m, disorder: m.disorder_name || "—", source: "Existing" });
                                                });
                                                Object.entries(invMutations.investigational).forEach(([disorder, muts]) => {
                                                  muts.forEach((m) => {
                                                    allRows.push({ ...m, disorder, source: "Investigational" });
                                                  });
                                                });

                                                // Search filter
                                                const searchLower = invSearch.toLowerCase();
                                                const filtered = searchLower
                                                  ? allRows.filter((m) =>
                                                      m.rsid.toLowerCase().includes(searchLower) ||
                                                      (m.mutation || "").toLowerCase().includes(searchLower) ||
                                                      String(m.position).includes(searchLower) ||
                                                      (m.classification || "").toLowerCase().includes(searchLower) ||
                                                      m.disorder.toLowerCase().includes(searchLower) ||
                                                      m.source.toLowerCase().includes(searchLower)
                                                    )
                                                  : allRows;

                                                // Sort
                                                const sorted = [...filtered].sort((a, b) => {
                                                  let cmp = 0;
                                                  switch (invSortCol) {
                                                    case "position":
                                                      cmp = a.position - b.position;
                                                      break;
                                                    case "classification": {
                                                      const ac = (a.classification || "zzz").toLowerCase();
                                                      const bc = (b.classification || "zzz").toLowerCase();
                                                      cmp = ac < bc ? -1 : ac > bc ? 1 : 0;
                                                      break;
                                                    }
                                                    case "uk_biobank": {
                                                      const av = a.uk_biobank === "yes" ? 0 : 1;
                                                      const bv = b.uk_biobank === "yes" ? 0 : 1;
                                                      cmp = av - bv;
                                                      break;
                                                    }
                                                    case "source": {
                                                      // Existing first by default
                                                      cmp = a.source === b.source ? 0 : a.source === "Existing" ? -1 : 1;
                                                      break;
                                                    }
                                                  }
                                                  return invSortDir === "asc" ? cmp : -cmp;
                                                });

                                                const handleSort = (col: typeof invSortCol) => {
                                                  if (invSortCol === col) {
                                                    setInvSortDir((d) => d === "asc" ? "desc" : "asc");
                                                  } else {
                                                    setInvSortCol(col);
                                                    setInvSortDir("asc");
                                                  }
                                                };

                                                const sortArrow = (col: typeof invSortCol) =>
                                                  invSortCol === col ? (invSortDir === "asc" ? " \u25B2" : " \u25BC") : "";

                                                const thStyle = (sortable: boolean): React.CSSProperties => ({
                                                  padding: "0.375rem 0.5rem",
                                                  textAlign: "left" as const,
                                                  color: "#64748b", background: "#f8fafc",
                                                  borderBottom: "2px solid #e2e8f0",
                                                  fontSize: "0.6875rem", fontWeight: 600,
                                                  textTransform: "uppercase" as const, letterSpacing: "0.05em",
                                                  cursor: sortable ? "pointer" : "default",
                                                  userSelect: "none" as const,
                                                  whiteSpace: "nowrap" as const,
                                                });

                                                return (
                                                  <>
                                                    {/* Header row: title + counts */}
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem", flexWrap: "wrap", gap: "0.5rem" }}>
                                                      <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#1A2940", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                                        Mutations for {selectedGene} ({filtered.length}{filtered.length !== allRows.length ? ` / ${allRows.length}` : ""})
                                                      </span>
                                                      <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.6875rem" }}>
                                                        <span style={{ color: "#64748b" }}>
                                                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: "#dbeafe", border: "1px solid #93c5fd", marginRight: 4, verticalAlign: "middle" }} />
                                                          Existing: {invMutations.existing.length}
                                                        </span>
                                                        <span style={{ color: "#64748b" }}>
                                                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: "#eef2ff", border: "1px solid #c7d2fe", marginRight: 4, verticalAlign: "middle" }} />
                                                          Investigational: {Object.values(invMutations.investigational).reduce((sum, arr) => sum + arr.length, 0)}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    {/* Search box */}
                                                    <div style={{ marginBottom: "0.5rem" }}>
                                                      <input
                                                        type="text"
                                                        placeholder="Search RSID, mutation, disorder..."
                                                        value={invSearch}
                                                        onChange={(e) => setInvSearch(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                          width: "100%",
                                                          padding: "0.4rem 0.625rem",
                                                          border: "1px solid #e2e8f0",
                                                          borderRadius: "0.375rem",
                                                          fontSize: "0.8125rem",
                                                          color: "#0f172a",
                                                          background: "#fff",
                                                          outline: "none",
                                                        }}
                                                      />
                                                    </div>
                                                    {/* Table */}
                                                    <div style={{ overflowX: "auto", maxHeight: "700px", overflowY: "auto" }}>
                                                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                                                        <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                                          <tr>
                                                            <th style={thStyle(false)}>RSID</th>
                                                            <th style={thStyle(false)}>Amino Acid Change</th>
                                                            <th style={{ ...thStyle(true), textAlign: "center" }} onClick={() => handleSort("position")}>Position{sortArrow("position")}</th>
                                                            <th style={thStyle(true)} onClick={() => handleSort("classification")}>Classification{sortArrow("classification")}</th>
                                                            <th style={thStyle(true)} onClick={() => handleSort("uk_biobank")}>Biobank{sortArrow("uk_biobank")}</th>
                                                            <th style={thStyle(false)}>Disorder</th>
                                                            <th style={thStyle(true)} onClick={() => handleSort("source")}>Source{sortArrow("source")}</th>
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          {sorted.length === 0 ? (
                                                            <tr>
                                                              <td colSpan={7} style={{ textAlign: "center", padding: "1rem", color: "#94a3b8", fontSize: "0.8125rem", fontStyle: "italic" }}>
                                                                No results match your search
                                                              </td>
                                                            </tr>
                                                          ) : sorted.map((m, idx) => {
                                                            const cls = m.classification?.toLowerCase() || "";
                                                            const colors = invClassificationColors[cls] || { bg: "#f8fafc", text: "#64748b" };
                                                            const isInv = m.source === "Investigational";
                                                            return (
                                                              <tr key={idx} style={{ background: isInv ? "#fafafe" : "transparent" }}>
                                                                <td style={{ padding: "0.375rem 0.5rem", color: "#0f172a", fontWeight: 500, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9" }}>{m.rsid}</td>
                                                                <td style={{ padding: "0.375rem 0.5rem", color: "#475569", borderBottom: "1px solid #f1f5f9" }}>{m.mutation || `${m.original_aa}\u2009\u2192\u2009${m.mutated_aa}`}</td>
                                                                <td style={{ padding: "0.375rem 0.5rem", textAlign: "center", color: "#0f172a", fontFamily: "monospace", borderBottom: "1px solid #f1f5f9" }}>{m.position}</td>
                                                                <td style={{ padding: "0.375rem 0.5rem", borderBottom: "1px solid #f1f5f9" }}>
                                                                  {m.classification ? (
                                                                    <span style={{ display: "inline-block", padding: "0.1rem 0.4rem", borderRadius: "3px", fontSize: "0.6875rem", fontWeight: 600, backgroundColor: colors.bg, color: colors.text, textTransform: "capitalize" }}>{m.classification}</span>
                                                                  ) : (
                                                                    <span style={{ color: "#cbd5e1", fontSize: "0.75rem" }}>—</span>
                                                                  )}
                                                                </td>
                                                                <td style={{ padding: "0.375rem 0.5rem", borderBottom: "1px solid #f1f5f9" }}>
                                                                  {m.uk_biobank === "yes" ? (
                                                                    <span style={{ display: "inline-block", padding: "0.1rem 0.4rem", borderRadius: "3px", fontSize: "0.6875rem", fontWeight: 600, backgroundColor: "#dbeafe", color: "#1e40af" }}>Yes</span>
                                                                  ) : m.uk_biobank ? (
                                                                    <span style={{ color: "#cbd5e1" }}>No</span>
                                                                  ) : (
                                                                    <span style={{ color: "#cbd5e1", fontSize: "0.75rem" }}>—</span>
                                                                  )}
                                                                </td>
                                                                <td style={{ padding: "0.375rem 0.5rem", borderBottom: "1px solid #f1f5f9", textTransform: "capitalize", color: "#475569", fontSize: "0.75rem" }}>{m.disorder}</td>
                                                                <td style={{ padding: "0.375rem 0.5rem", borderBottom: "1px solid #f1f5f9" }}>
                                                                  <span style={{
                                                                    display: "inline-block",
                                                                    padding: "0.1rem 0.4rem",
                                                                    borderRadius: "3px",
                                                                    fontSize: "0.6875rem",
                                                                    fontWeight: 600,
                                                                    backgroundColor: isInv ? "#eef2ff" : "#dbeafe",
                                                                    color: isInv ? "#4338ca" : "#1e40af",
                                                                  }}>{m.source}</span>
                                                                </td>
                                                              </tr>
                                                            );
                                                          })}
                                                        </tbody>
                                                      </table>
                                                    </div>
                                                  </>
                                                );
                                              })() : (
                                                <div style={{ textAlign: "center", padding: "0.75rem", color: "#94a3b8", fontSize: "0.8125rem", fontStyle: "italic" }}>
                                                  No mutations found for this investigational drug
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                });
                              })()}
                              {drugs.length === 0 && invDrugs.length === 0 && (
                                <tr>
                                  <td colSpan={2} className="no-data-message">
                                    No drugs found for this gene in the selected disorder
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div>
                        <div className="section-title">Natural/Endogenous Ligands</div>
                        <div className="table-responsive">
                          {ligandsLoading ? (
                            <div className="no-data-message">Loading ligands...</div>
                          ) : (
                            <table className="table at-glance-table mb-0">
                              <thead>
                                <tr>
                                  <th>Ligand Name</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ligands.length > 0 ? (
                                  <tr>
                                    <td>
                                      {ligands.map((ligand) => ligand.ligand_name).join(", ")}
                                    </td>
                                  </tr>
                                ) : (
                                  <tr>
                                    <td className="no-data-message">
                                      No ligands found for this gene
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    </section>
                      </div>{/* /overview-map-left */}

                      {/* Right column: geographical distribution map */}
                      <div className="overview-map-right">
                        <h3 style={{ color: "#64748b", fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>
                          Geographical Distribution
                        </h3>
                        <FrequencyMap geneName={selectedGene!} rows={gnomadRows ?? undefined} />
                      </div>
                    </div>{/* /overview-map-grid */}

                    <div style={{ height: "1px", backgroundColor: "#e5e7eb", margin: "1.5rem 0" }} />

                    {/* Variant Annotations */}
                    <h2
                      style={{
                        color: "#0f172a",
                        fontSize: "0.9375rem",
                        fontWeight: 700,
                        marginBottom: "0.75rem",
                      }}
                    >
                      Variant Annotations
                    </h2>

                    {/* Lollipop / Frequency Plot */}
                    <div className="mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h3 style={{ color: "#64748b", fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.04em", margin: 0 }}>
                          Protein Mutation Visualization
                        </h3>
                        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '6px', padding: '2px' }}>
                          <button
                            onClick={() => selectPlotView('lollipop')}
                            style={{
                              padding: '0.3rem 0.75rem',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              border: 'none',
                              borderRadius: '5px',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              background: plotView === 'lollipop' ? '#1A2940' : 'transparent',
                              color: plotView === 'lollipop' ? '#fff' : '#64748b',
                            }}
                          >
                            Mutation Plot
                          </button>
                          <button
                            onClick={() => selectPlotView('frequency')}
                            style={{
                              padding: '0.3rem 0.75rem',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              border: 'none',
                              borderRadius: '5px',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              background: plotView === 'frequency' ? '#1A2940' : 'transparent',
                              color: plotView === 'frequency' ? '#fff' : '#64748b',
                            }}
                          >
                            Frequency Plot
                          </button>
                        </div>
                      </div>
                      <div className="py-2">
                        {targetData[0]?.mutations &&
                        Object.keys(targetData[0].mutations).length > 0 ? (
                          <>
                            {/* Both plots stay mounted once opened; we only
                                toggle visibility so switching tabs is instant
                                and never re-fetches/re-parses data. */}
                            <div style={{ display: plotView === 'lollipop' ? 'block' : 'none' }}>
                              {mountedPlots.has('lollipop') && (
                                <LollipopPlot
                                  mutations={preparedMutations}
                                  proteinLength={proteinLength}
                                  uniprotId={targetData[0]?.uniprot_id}
                                  sharedFilters={sharedFilters}
                                  setSharedFilters={setSharedFilters}
                                  domains={domains ?? undefined}
                                />
                              )}
                            </div>
                            <div style={{ display: plotView === 'frequency' ? 'block' : 'none' }}>
                              {mountedPlots.has('frequency') && (
                                <FrequencyBarPlot
                                  geneName={selectedGene!}
                                  mutations={preparedMutations}
                                  proteinLength={proteinLength}
                                  rows={gnomadRows ?? undefined}
                                />
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <p>No mutation data available for visualization</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ height: "1px", backgroundColor: "#d1d5db", margin: "1.5rem 0" }} />

                    {/* RSID Table */}
                    <div>
                      <h3 style={{ color: "#64748b", fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.04em" }}>
                        Mutation Details
                      </h3>
                      <RSIDTABLE
                        targetName={String(selectedGene)}
                        data={targetData}
                        sharedFilters={sharedFilters}
                        setSharedFilters={setSharedFilters}
                        isGPCR={isGPCR}
                        gnomadRows={gnomadRows ?? undefined}
                        domains={domains ?? undefined}
                      />
                    </div>
                  </div>
                </div>

                {/* Show / Hide Target toggle tab */}
                {targetData[0]?.pdb_id && (
                  <button
                    type="button"
                    className={`structure-toggle ${showStructure ? 'open' : ''}`}
                    onClick={toggleStructure}
                    aria-expanded={showStructure}
                    title={showStructure ? 'Hide protein structure' : 'Show protein structure'}
                  >
                    <span className="chevron">{showStructure ? '▸' : '◂'}</span>
                    {showStructure ? 'Hide Target' : 'Show Target'}
                  </button>
                )}

                {/* Right: collapsible protein structure */}
                <div className={`detail-right ${showStructure ? 'open' : ''}`}>
                  <div className="structure-viewer" style={{ flex: 1 }}>
                    {structureMounted && <PDBViewer pdb_id={targetData[0]?.pdb_id} />}
                  </div>
                  {targetData[0]?.pdb_id && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 8px',
                      borderTop: '1px solid #e2e8f0',
                      background: '#f8fafc',
                      flexShrink: 0,
                    }}>
                      <a
                        href={`https://www.rcsb.org/structure/${targetData[0].pdb_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: '0.7rem',
                          color: '#64748b',
                          textDecoration: 'none',
                          fontWeight: 500,
                        }}
                      >
                        RCSB PDB: {targetData[0].pdb_id}
                      </a>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <a
                          href={`https://files.rcsb.org/download/${targetData[0].pdb_id.toUpperCase()}.pdb`}
                          download={`${targetData[0].pdb_id.toUpperCase()}.pdb`}
                          style={{
                            fontSize: '0.65rem',
                            color: '#3b82f6',
                            textDecoration: 'none',
                            fontWeight: 500,
                          }}
                          title="Download PDB file"
                        >
                          Download .pdb
                        </a>
                        <span style={{ color: '#cbd5e1', fontSize: '0.65rem' }}>|</span>
                        <button
                          onClick={() => {
                            const canvas = document.querySelector('.molstar-wrapper canvas') as HTMLCanvasElement;
                            if (canvas) {
                              const link = document.createElement('a');
                              link.download = `${targetData[0].pdb_id.toUpperCase()}.png`;
                              link.href = canvas.toDataURL('image/png');
                              link.click();
                            }
                          }}
                          style={{
                            fontSize: '0.65rem',
                            color: '#3b82f6',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            fontWeight: 500,
                            cursor: 'pointer',
                          }}
                          title="Download structure image"
                        >
                          Download .png
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="target-detail-loader">
              <p style={{ color: "#6b7280" }}>No data available for this target</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Disorder;
